import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, ButtonGroup, Col, Row } from 'reactstrap';

import Analytics from '../../../analytics/Analytics';
import globalTranslations from '../../../i18n/globalTranslations';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import ProductImageModel from '../../../models/ProductImage';
import ProductListProduct from '../../../models/ProductListProduct';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import ProductStore from '../../../store/ProductStore';
import UIStore from '../../../store/UIStore';
import ProductClass from '../../../types/ProductClass';
import ProductRecurringOrderType from '../../../types/ProductRecurringOrderType';
import RequestState from '../../../types/RequestState';
import { productCountValid, quantityValid } from '../../../util/formValidators';
import {
  calculateNearestProductQuantityWithQuantityStep,
  calculateProductPackageSizeWithQuantity,
  roundWithPrecision,
} from '../../../util/number';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import QuantityFieldGroup from '../../common/QuantityFieldGroup';
import FieldError from '../../form/FieldError';
import Price from '../../product/Price';
import ProductAvailabilityText from '../../product/ProductAvailabilityText';
import ProductImage from '../../product/ProductImage';
import ProductPagePrice from '../../product/ProductPagePrice';
import ProductPrice from '../../product/ProductPrice';

const CUSTOM_ASPECT_RATIO = '120';

@observer
class WishListProductRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      totalPrice: 0,
      product: this.props.product,
    };

    this.form = null;
    this.maxQuantity = 99999;
    this.defaultValue = 0;
    this.accessToken = null;
    this.quantityField = null;

    this.defaultValue = calculateProductPackageSizeWithQuantity(
      this.getQuantityStep(this.state.product),
      this.state.product.quantity
    );

    const formField = this.createQuantityFormFields(this.props.product);
    this.initializeQuantityFormField(formField);
  }

  componentDidMount() {
    this.calculateTotalPrice();
  }

  getAccessToken = () => {
    const { match } = this.props;
    if (
      match.params.hasOwnProperty('accessToken') &&
      match.params.accessToken
    ) {
      this.accessToken = match.params.accessToken.length;
    }
  };

  createQuantityFormFields = (product) => {
    let formFields = {};
    formFields['quantity' + product.product_id] = createFormField(
      {
        value: String(product.quantity),
      },
      [
        quantityValid(this.maxQuantity),
        productCountValid(this.getQuantityStep(product)),
      ]
    );
    return formFields;
  };

  initializeQuantityFormField = (formFields) => {
    this.form = null;
    this.form = createForm(formFields);
  };

  renderProductImageOnLargeDisplays = (product) => {
    const element = (
      <ProductImage
        product={product}
        productImage={this.createProductImage(product)}
        size="small"
        forceAspectRatio={true}
        overrideConfigStoreAspectRatio={true}
        customAspectRatio={CUSTOM_ASPECT_RATIO}
        lazyLoading={false}
      />
    );
    return product.link ? this.renderElementWithLinkIfExists(element) : element;
  };

  renderProductName = (product) => {
    const element = product.actual_code + ' - ' + product.name;
    return product.link ? this.renderElementWithLinkIfExists(element) : element;
  };

  renderElementWithLinkIfExists = (element) => (
    <MaybeExternalLink to={this.state.product.link}>
      {element}
    </MaybeExternalLink>
  );

  renderProductAvailabilityText = (product) => (
    <div>
      <ProductAvailabilityText availabilityHtml={product.availability_html} />
    </div>
  );

  renderProductVariations = (product) => {
    return (
      <ul className="WishListProductRow_product-variations">
        {product.variations.map((variation, i) => {
          if (!variation) {
            return null;
          }

          return <li key={i}>{variation}</li>;
        })}
      </ul>
    );
  };

  createProductImage = (product) => {
    const url = product.image;
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: this.getProductIdByProductClass(product),
          sizes: {
            small: url,
          },
        })
      : null;
  };

  getProductIdByProductClass = (product) => {
    return product.class === ProductClass.MULTI ||
      product.class === ProductClass.COLLECTION
      ? product.id
      : product.product_id;
  };

  renderAddToCartQuantity = () => {
    if (!this.state.product.price_info) {
      return null;
    }

    this.quantityField = this.form.fields.get(
      'quantity' + this.state.product.product_id
    );
    return (
      <QuantityFieldGroup
        key={this.state.product.product_id}
        disabled={!!this.ifQuantityFieldDisabled()}
        className={
          'WishListProductRow__product-list-quantity-group ' +
          this.state.product.product_id
        }
        field={this.quantityField}
        quantityStep={this.getQuantityStep(this.state.product)}
        defaultValue={this.defaultValue}
        maxQuantity={this.maxQuantity}
        unit={this.state.product.stock_unit}
        onUpdateProductQuantity={this.updateProductQuantity.bind(this)}
      />
    );
  };

  ifQuantityFieldDisabled = () => {
    const { match } = this.props;
    return (
      match.params.hasOwnProperty('accessToken') && match.params.accessToken
    );
  };

  getQuantityStep = (product) => {
    return product && product.package_size > 1 ? product.package_size : 1;
  };

  showDiscountPriceIfExists = () => {
    const { product } = this.state;

    if (!product || !product.price_info) {
      return null;
    }

    const productListProduct = ProductListProduct.create(product);
    const price = (
      <ProductPrice
        className={'WishlistProductRow__product-row-product-page-price'}
        priceInfo={productListProduct.price_info}
      />
    );
    return (
      <ProductPagePrice
        className={'WishlistProductRow__product-row-product-page-price'}
        price={price}
        product={productListProduct}
      />
    );
  };

  renderTotalPriceAndQuantity = (product) => {
    return (
      <div className="WishListProductRow__product-list-price-and-quantity">
        {this.renderAddToCartQuantity(product)}
        {this.showTotalPriceIfExists(product)}
        <FieldError
          field={this.form.fields.get(
            'quantity' + this.state.product.product_id
          )}
        />
      </div>
    );
  };

  showTotalPriceIfExists = () => {
    const { product, totalPrice } = this.state;
    if (
      !product ||
      !product.price_info ||
      !this.quantityField.value ||
      this.quantityField.value === '0'
    ) {
      return null;
    }

    return (
      <Price
        className="WishListProductRow__product-row-total-price"
        price={totalPrice}
      />
    );
  };

  renderWishListProductRowButtons = (product) => {
    const { accountStore, configStore, productStore } = this.props;
    const buyingAllowed = !(
      configStore.recurringOrder.enabled &&
      accountStore.loggedIn &&
      product.recurring_order_type ===
        ProductRecurringOrderType.RECURRING_ORDERS_ONLY
    );

    return (
      <div className="WishListProductRow__product-list-buttons">
        <ButtonGroup vertical>
          {buyingAllowed && (
            <Button
              color="success"
              disabled={this.validateAddToCartButton()}
              onClick={this.productAddToCart.bind(this, product)}
            >
              <FormattedMessage {...globalTranslations.addToCartButton} />
            </Button>
          )}
          <Button
            disabled={
              !!this.accessToken ||
              productStore.productListState === RequestState.LOADING
            }
            color="primary"
            onClick={this.deleteProduct.bind(this, product)}
          >
            <FormattedMessage
              id="wishlist.DeleteProduct"
              defaultMessage="Delete"
            />
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  validateAddToCartButton = () => {
    return (
      this.form.valid === false ||
      !this.state.product.price_info ||
      this.quantityField.value === '0' ||
      !this.quantityField.value
    );
  };

  updateProductQuantity = (packageQuantity) => {
    const { accountStore, product } = this.props;
    if (accountStore.account.is_logged) {
      this.updateProductListIfLoggedIn(product, packageQuantity);
    } else {
      this.updateLocalProductList(product, packageQuantity);
    }
  };

  updateProductListIfLoggedIn = (product, packageQuantity) => {
    const { productStore } = this.props;
    const quantity = calculateNearestProductQuantityWithQuantityStep(
      packageQuantity,
      this.getQuantityStep(product)
    );
    productStore
      .saveProduct({
        extendedId: product.id,
        quantity,
      })
      .catch((err) => console.error(err));

    // This needs another solution to calculate prices.
    // This causes superficial product list to be kept in local storage just to count price.
    this.updateLocalPackageQuantity(quantity, packageQuantity);
    this.calculateTotalPrice();
  };

  updateLocalProductList = (product, packageQuantity) => {
    const newQuantity = calculateNearestProductQuantityWithQuantityStep(
      packageQuantity,
      this.getQuantityStep(product)
    );
    this.updateLocalPackageQuantity(newQuantity, packageQuantity);
  };

  calculateTotalPrice = () => {
    const { product } = this.state;

    if (!product || !product.price_info) {
      return null;
    }

    this.setState({
      totalPrice: product.quantity * product.price_info.price.with_tax,
    });

    if (product.package_size) {
      this.setState({
        totalPrice:
          product.quantity *
          product.package_size *
          product.price_info.price.with_tax,
      });
    }
  };

  updateLocalPackageQuantity = (quantity, packageQuantity) => {
    const product = {
      ...this.state.product,
      quantity: Math.ceil(quantity),
    };
    this.setState({ product }, () => {
      this.props.onStorageUpdate(this.state.product);
      this.calculateTotalPrice();
    });
    this.defaultValue = packageQuantity;
  };

  productAddToCart = () => {
    const { analytics, accountStore, cartStore } = this.props;
    const { product } = this.state;
    const withTax = accountStore.showPricesWithTax;
    const wishlistProduct = ProductListProduct.create(product);
    const quantity = wishlistProduct.quantity;

    const activeProductId = wishlistProduct.id;

    const analyticsProduct = {
      product: wishlistProduct,
      quantity: wishlistProduct.getAnalyticsValues(withTax).quantity,
      activeProductId,
    };

    this.form.validate();
    if (this.form.valid) {
      cartStore
        .addToCart(activeProductId, quantity, [], [], wishlistProduct)
        .then(() => {
          const totalValue = wishlistProduct.getAnalyticsValues(withTax).price;

          analytics.addToCart(
            [analyticsProduct],
            roundWithPrecision(totalValue, 4)
          );
          analytics.cartStatus({
            cart: cartStore.cart,
          });
        })
        .catch((err) => console.error(err));
    }
  };

  deleteProduct = (product) => {
    const { accountStore, productStore, onProductDelete } = this.props;
    if (accountStore.account.is_logged) {
      const localProduct = { extendedId: product.id };
      productStore
        .deleteProduct(localProduct)
        .then(() => onProductDelete())
        .catch((err) => console.error(err));
    } else {
      this.deleteLocalProduct(product);
    }
  };

  deleteLocalProduct = (product) => {
    this.props.onProductDelete(product);
  };

  render() {
    const { uiStore, product } = this.props;
    this.getAccessToken();

    return (
      <Row className="WishListProductRow WishListProductRow__product-list-row">
        {uiStore.isDesktop && (
          <Col lg={2}>
            <div className="WishListProductRow__product-image">
              {this.renderProductImageOnLargeDisplays(product)}
            </div>
          </Col>
        )}
        <Col xs={12} lg={2} className="WishListProductRow__product-list-col">
          <div className="WishListProductRow__product-list-product">
            <div className="WishListProductRow__product-name">
              {this.renderProductName(product)}
              {this.renderProductAvailabilityText(product)}
              {this.renderProductVariations(product)}
              {(uiStore.isMobile || uiStore.isTablet) &&
                this.showDiscountPriceIfExists(product.price_info)}
              {(uiStore.isMobile || uiStore.isTablet) &&
                this.renderTotalPriceAndQuantity(product)}
            </div>
          </div>
        </Col>
        {uiStore.isDesktop && (
          <Col md={2} className="WishListProductRow__product-list-col">
            {this.showDiscountPriceIfExists(product.price_info)}
          </Col>
        )}
        {uiStore.isDesktop && (
          <Col md={4} className="WishListProductRow__product-list-col">
            {this.renderTotalPriceAndQuantity(product)}
          </Col>
        )}
        <Col xs={12} lg={2} className="WishListProductRow__product-list-col">
          {this.renderWishListProductRowButtons(product)}
        </Col>
      </Row>
    );
  }
}

WishListProductRow.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  match: PropTypes.object.isRequired,
  onProductDelete: PropTypes.func.isRequired,
  onStorageUpdate: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
};

export default inject(
  'accountStore',
  'cartStore',
  'configStore',
  'productStore',
  'uiStore',
  'analytics'
)(WishListProductRow);
